class RangeFacet extends HTMLElement {
  constructor() {
    super();
    this.setupSliders();
    this.attachEventListeners();
  }

  setupSliders() {
    this.minSlider = this.querySelector(".range-facet__slider--min");
    this.maxSlider = this.querySelector(".range-facet__slider--max");

    // Check if sliders exist before accessing their dataset
    if (!this.minSlider || !this.maxSlider) return;

    this.minInput = this.querySelector(`#${this.minSlider.dataset.target}`);
    this.maxInput = this.querySelector(`#${this.maxSlider.dataset.target}`);
    this.sliderFill = this.querySelector(".range-facet__slider-fill");
    this.displayRange = this.querySelector(".range-facet__display");
    this.minTooltip = this.querySelector(".range-facet__tooltip--min");
    this.maxTooltip = this.querySelector(".range-facet__tooltip--max");
    this.minValue = parseInt(this.minSlider.min);
    this.maxValue = parseInt(this.minSlider.max);

    if (this.classList.contains('range-facet--flavor')) this.updateFlavor();

    if (!this.minInput || !this.maxInput) return;

    // Initialize slider positions
    this.updateSliderVisuals();
    this.updateTooltipContent();
    this.updateTooltipPositions();
  }

  attachEventListeners() {
    if (!this.minSlider || !this.maxSlider || !this.minInput || !this.maxInput)
      return;

    // Prevent overlap of slider handles
    this.minSlider.addEventListener("input", this.preventOverlap.bind(this));
    this.maxSlider.addEventListener("input", this.preventOverlap.bind(this));

    // Slider events
    this.minSlider.addEventListener(
      "input",
      this.handleMinSliderChange.bind(this)
    );
    this.maxSlider.addEventListener(
      "input",
      this.handleMaxSliderChange.bind(this)
    );

    // Input field events
    this.minInput.addEventListener(
      "input",
      this.handleMinInputChange.bind(this)
    );
    this.maxInput.addEventListener(
      "input",
      this.handleMaxInputChange.bind(this)
    );
    this.minInput.addEventListener("blur", this.handleMinInputBlur.bind(this));
    this.maxInput.addEventListener("blur", this.handleMaxInputBlur.bind(this));

    // Tooltip events
    this.attachTooltipEvents();
  }

  handleMinSliderChange(event) {
    const value = parseInt(event.target.value);
    this.minInput.value = this.formatCurrency(value);
    this.updateSliderVisuals();
    this.updateDisplayRange();
    this.updateTooltipContent();
    this.updateTooltipPositions();
  }

  handleMaxSliderChange(event) {
    const value = parseInt(event.target.value);
    this.maxInput.value = this.formatCurrency(value);
    this.updateSliderVisuals();
    this.updateDisplayRange();
    this.updateTooltipContent();
    this.updateTooltipPositions();
  }

  handleMinInputChange(event) {
    const value = this.parseCurrency(event.target.value);
    if (!isNaN(value)) {
      this.minSlider.value = Math.max(
        this.minValue,
        Math.min(value, parseInt(this.maxSlider.value))
      );
      this.updateSliderVisuals();
      this.updateDisplayRange();
      this.updateTooltipContent();
      this.updateTooltipPositions();
    }
  }

  handleMaxInputChange(event) {
    const value = this.parseCurrency(event.target.value);
    if (!isNaN(value)) {
      this.maxSlider.value = Math.min(
        this.maxValue,
        Math.max(value, parseInt(this.minSlider.value))
      );
      this.updateSliderVisuals();
      this.updateDisplayRange();
      this.updateTooltipContent();
      this.updateTooltipPositions();
    }
  }

  handleMinInputBlur(event) {
    const value = this.parseCurrency(event.target.value);
    if (isNaN(value) || value < this.minValue) {
      event.target.value = this.formatCurrency(parseInt(this.minSlider.value));
    }
  }

  handleMaxInputBlur(event) {
    const value = this.parseCurrency(event.target.value);
    if (isNaN(value) || value > this.maxValue) {
      event.target.value = this.formatCurrency(parseInt(this.maxSlider.value));
    }
  }

  preventOverlap(event) {
    const minVal = parseInt(this.minSlider.value);
    const maxVal = parseInt(this.maxSlider.value);

    if (minVal >= maxVal) {
      if (event.target === this.minSlider) {
        this.minSlider.value = maxVal - 1;
      } else {
        this.maxSlider.value = minVal + 1;
      }
    }
  }

  updateSliderVisuals() {
    if (!this.minSlider || !this.maxSlider || !this.sliderFill) return;

    const minVal = parseInt(this.minSlider.value);
    const maxVal = parseInt(this.maxSlider.value);

    const minPercent =
      ((minVal - this.minValue) / (this.maxValue - this.minValue)) * 100;
    const maxPercent =
      ((maxVal - this.minValue) / (this.maxValue - this.minValue)) * 100;

    this.sliderFill.style.left = `${minPercent}%`;
    this.sliderFill.style.width = `${maxPercent - minPercent}%`;
  }

  updateDisplayRange() {
    if (!this.minSlider || !this.maxSlider) return;

    const minVal = parseInt(this.minSlider.value);
    const maxVal = parseInt(this.maxSlider.value);

    if (this.displayRange) {
      this.displayRange.textContent = `${this.formatCurrencyDisplay(minVal)} - ${this.formatCurrencyDisplay(maxVal)}`;
    }
  }

  parseCurrency(value) {
    // Remove currency symbols and parse number
    return parseInt(value.toString().replace(/[^\d]/g, "")) || 0;
  }

  formatCurrency(value) {
    // Format number with thousand separators for input field
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  }

  formatCurrencyDisplay(value) {
    // Format for display with currency symbol
    return `${this.formatCurrency(value)} đ`;
  }

  attachTooltipEvents() {
    if (!this.minSlider || !this.maxSlider) return;

    // Show tooltips on mouseenter/focus
    this.minSlider.addEventListener("mouseenter", () =>
      this.showTooltip("min")
    );
    this.maxSlider.addEventListener("mouseenter", () =>
      this.showTooltip("max")
    );
    this.minSlider.addEventListener("focus", () => this.showTooltip("min"));
    this.maxSlider.addEventListener("focus", () => this.showTooltip("max"));

    // Hide tooltips on mouseleave/blur
    this.minSlider.addEventListener("mouseleave", () =>
      this.hideTooltip("min")
    );
    this.maxSlider.addEventListener("mouseleave", () =>
      this.hideTooltip("max")
    );
    this.minSlider.addEventListener("blur", () => this.hideTooltip("min"));
    this.maxSlider.addEventListener("blur", () => this.hideTooltip("max"));

    // Show tooltips during drag
    this.minSlider.addEventListener("mousedown", () => this.showTooltip("min"));
    this.maxSlider.addEventListener("mousedown", () => this.showTooltip("max"));

    // Hide tooltips after drag
    document.addEventListener("mouseup", () => {
      this.hideTooltip("min");
      this.hideTooltip("max");
    });
  }

  showTooltip(type) {
    const tooltip = type === "min" ? this.minTooltip : this.maxTooltip;
    if (tooltip) {
      tooltip.classList.add("show");
    }
  }

  hideTooltip(type) {
    const tooltip = type === "min" ? this.minTooltip : this.maxTooltip;
    if (tooltip) {
      tooltip.classList.remove("show");
    }
  }

  updateTooltipContent() {
    if (!this.minSlider || !this.maxSlider) return;

    const minVal = parseInt(this.minSlider.value);
    const maxVal = parseInt(this.maxSlider.value);

    if (this.minTooltip) {
      const minContent = this.minTooltip.querySelector(
        ".range-facet__tooltip-content"
      );
      if (minContent) {
        minContent.textContent = this.formatCurrencyDisplay(minVal);
      }
    }

    if (this.maxTooltip) {
      const maxContent = this.maxTooltip.querySelector(
        ".range-facet__tooltip-content"
      );
      if (maxContent) {
        maxContent.textContent = this.formatCurrencyDisplay(maxVal);
      }
    }
  }

  updateTooltipPositions() {
    if (!this.minSlider || !this.maxSlider) return;

    const minVal = parseInt(this.minSlider.value);
    const maxVal = parseInt(this.maxSlider.value);

    const minPercent =
      ((minVal - this.minValue) / (this.maxValue - this.minValue)) * 100;
    const maxPercent =
      ((maxVal - this.minValue) / (this.maxValue - this.minValue)) * 100;

    if (this.minTooltip) {
      this.minTooltip.style.left = `${minPercent}%`;
      // Adjust transform to prevent going off-screen
      if (minPercent < 10) {
        this.minTooltip.style.transform = "translateX(0)";
      } else {
        this.minTooltip.style.transform = "translateX(-50%)";
      }
    }

    if (this.maxTooltip) {
      this.maxTooltip.style.left = `${maxPercent}%`;
      // Adjust transform to prevent going off-screen
      if (maxPercent > 90) {
        this.maxTooltip.style.transform = "translateX(-100%)";
      } else {
        this.maxTooltip.style.transform = "translateX(-50%)";
      }
    }
  }

  updateFlavor() {
    this.minSlider.addEventListener("input", this.preventOverlap.bind(this));
    this.maxSlider.addEventListener("input", this.preventOverlap.bind(this));
    this.minSlider.addEventListener("input", this.handleSliderFlavorChange.bind(this));
    this.maxSlider.addEventListener("input", this.handleSliderFlavorChange.bind(this));
  }

  handleSliderFlavorChange() {
    this.updateSliderVisuals();
    this.updateFlavorFilter();
  }

  updateFlavorFilter() {
    const facetsWrap = this.parentElement;
    const minVal = parseInt(this.minSlider.value);
    const maxVal = parseInt(this.maxSlider.value);
    const arr = Array.from({ length: maxVal - minVal + 1 }, (_, i) => i + minVal);

    for (let i = 0; i <= 10; i++) {
      const box = facetsWrap.querySelector(`[type="checkbox"][value="${i}"]:not([disabled])`);
      if (box) arr.includes(i) ? box.checked = true : box.checked = false;
    }

    this.updateFlavorDisplay(arr[0], arr[arr.length - 1]);
  }

  updateFlavorDisplay(min, max) {
    const facetsWrap = this.parentElement;
    const displayMin = facetsWrap.querySelector('.facets-flavor__display-min');
    const displayMax = facetsWrap.querySelector('.facets-flavor__display-max');

    displayMin.innerText = min;
    displayMax.innerText = max;
  }
}

// Register the custom element
customElements.define("range-facet", RangeFacet);

// Enhanced PriceRange class integration
if (typeof PriceRange !== "undefined") {
  const OriginalPriceRange = PriceRange;

  class PriceRangeEnhanced extends OriginalPriceRange {
    constructor() {
      super();
      // Initialize range facet if it exists within this component
      const rangeFacet = this.querySelector(".range-facet");
      if (rangeFacet) {
        this.rangeFacetComponent = new RangeFacet();
      }
    }

    onRangeChange(event) {
      // Call original method
      super.onRangeChange(event);

      // Update range facet display if it exists
      if (this.rangeFacetComponent) {
        this.rangeFacetComponent.updateDisplayRange();
      }
    }
  }

  // Replace the original PriceRange
  window.PriceRange = PriceRangeEnhanced;
}
